import {
  ErrorCause,
  PageResponse,
  PaginationParams,
  Request,
  RequestQueryOptions,
  EngineRequestResultDBData
} from 'vrlcm-gui-types';
import {
  extractJobResultData,
  Http,
  isRequestInprogress,
  promisePoll
} from 'vrlcm-gui-utils';
import { RequestApiUri } from './uri';

const REQUEST_STATUS_POLL_DELAY_MS = 1200;

export class RequestApi {
  public static async getAllRequests() {
    const response = await Http.get<Request[]>(RequestApiUri.REQUESTS_URL);
    return response.data;
  }

  public static async getRequests(paginationParams: PaginationParams) {
    const response = await Http.get<PageResponse<Request>>(
      RequestApiUri.GET_REQUESTS_URL(paginationParams)
    );
    return response.data;
  }

  public static async getRequest<T>(requestId: string) {
    const response = await Http.get<Request<T>>(
      RequestApiUri.REQUEST_URL(requestId)
    );
    return response.data;
  }

  public static async getRequestWithQueryOptions(
    options?: RequestQueryOptions,
    postInitFunction?: (cancelFunction: () => void) => void
  ) {
    const cancelFunctions: Array<() => void> = [];
    if (postInitFunction) {
      const cancel = () => cancelFunctions.forEach(cf => cf());
      postInitFunction(cancel);
    }
    const response = await RequestApi.getRequests({
      sortBy: options ? options.sortBy : undefined,
      sortByDirection: options ? options.sortByDirection : undefined,
      requestName:
        options && options.filter && options.filter.requestTypes
          ? options.filter.requestTypes.join(',')
          : undefined,
      state:
        options && options.filter && options.filter.requestStates
          ? options.filter.requestStates.join(',')
          : undefined,
      currentPage: 1,
      pageSize:
        options && options.limit !== undefined
          ? options.limit
          : 1000000 /* Case where we need to get all requests but API is begging for pageSize.
          Keeping the pageSize very very high */
    });
    return response.content;
  }

  public static async pollRequestForCompletion<T>(
    requestId: string,
    delay = REQUEST_STATUS_POLL_DELAY_MS,
    postInitFunction?: (cancelFunction: () => void) => void
  ) {
    return promisePoll<Request<T>>(
      RequestApi.getRequest,
      [requestId],
      (request: Request<T>) => !isRequestInprogress(request.state),
      delay,
      postInitFunction
    );
  }

  public static async pollRequestForCompletionAndExtractEngineResult<RD = any>(
    requestId: string,
    extractEngineRequestResultData?: <T>(
      engineRequestResultDBData: EngineRequestResultDBData
    ) => T | undefined,
    delay = REQUEST_STATUS_POLL_DELAY_MS,
    postInitFunction?: (cancelFunction: () => void) => void
  ) {
    const request = await RequestApi.pollRequestForCompletion(
      requestId,
      delay,
      postInitFunction
    );
    return {
      state: request.state,
      result: extractJobResultData<RD>(
        request.executionId,
        request.resultSet,
        extractEngineRequestResultData
      )
    };
  }

  public static async deleteRequest(requestId: string) {
    await Http.delete(RequestApiUri.REQUEST_URL(requestId));
  }

  public static async cancelRequest(requestId: string) {
    await Http.post(RequestApiUri.CANCEL_REQUEST_URL(requestId));
  }

  public static async getEnvironmentActiveRequests(environmentId: string) {
    const response = await Http.get<string[]>(
      RequestApiUri.GET_ENVIRONMENT_ACTIVE_REQUESTS_URL(environmentId)
    );
    return response.data;
  }

  public static async submitRetryRequest(
    requestId: string,
    causeList: ErrorCause[]
  ) {
    await Http.patch(RequestApiUri.RETRY_REQUEST_URL(requestId), causeList);
  }
}
